"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, CheckCircle2, Sparkles } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const auditPoints = [{
  title: "Crawl and indexation",
  description: "Evaluating robots.txt directives, parameter filtering, crawl traps and duplicate URL structures."
}, {
  title: "Product and category pages",
  description: "Reviewing title tags, unique copy, internal linking paths and search-intent alignment across your catalogue."
}, {
  title: "Structured data and architecture",
  description: "Checking Schema.org product and offer markup, breadcrumbs and category taxonomy hierarchy."
}];
export default function Ecommerceaudit() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="ecommerceauditcta" data-nav="light" className="bg-background text-foreground py-20 lg:py-28 relative overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          {/* Left Column: Heading, intro & review scope */}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="lg:col-span-7 space-y-6">
            <div className="inline-flex items-center gap-2">
              <Badge variant="secondary" className="px-3.5 py-1.5 rounded-full text-xs sm:text-sm font-semibold uppercase tracking-wider text-primary bg-secondary border-none">
                <Sparkles className="size-3.5 text-accent" />
                <span>Next Steps</span>
              </Badge>
            </div>

            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-default text-foreground leading-tight tracking-tight">
              Ready to strengthen your online store&apos;s organic performance?
            </h2>

            <p className="text-base sm:text-lg text-muted-foreground font-default leading-relaxed">
              If your ecommerce store is not getting the organic traffic and revenue it should, I would like to hear about your priorities. Whether you are a UK-based online business or an international English-speaking ecommerce brand, I work remotely and adapt to your platform and team setup. The next step is low-commitment: a free consultation to discuss your store&apos;s search challenges, or a free high-level audit of your website&apos;s organic performance.
            </p>

            <div className="space-y-4 pt-2">
              <p className="text-sm font-semibold uppercase tracking-wider text-foreground">
                What I review in an audit:
              </p>
              <div className="space-y-3.5">
                {auditPoints.map((point, i) => <div key={i} data-index={i} className="flex items-start gap-3">
                    <div className="mt-1 size-5 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0">
                      <CheckCircle2 className="size-3.5" />
                    </div>
                    <div>
                      <h3 className="text-base font-semibold text-foreground font-default">
                        {point.title}
                      </h3>
                      <p className="text-sm text-muted-foreground font-default">
                        {point.description}
                      </p>
                    </div>
                  </div>)}
              </div>
            </div>

            <div className="pt-4 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-md px-6 text-base font-medium shadow-sm">
                <Link to="/ContactPage">
                  <span>Book a Free SEO Consultation</span>
                  <ArrowRight className="size-4 ml-1.5" />
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="border-border text-foreground hover:bg-muted rounded-md px-6 text-base font-medium">
                <Link to="/ContactPage?topic=free-seo-audit">
                  <span>Get a Free SEO Audit</span>
                </Link>
              </Button>
            </div>

            <div className="pt-2">
              <Link to="/CaseStudiesPage" className="text-sm font-medium text-primary hover:text-primary/80 underline underline-offset-4 inline-flex items-center gap-1.5">
                <span>Explore my case studies</span>
                <ArrowRight className="size-3.5" />
              </Link>
            </div>
          </motion.div>

          {/* Right Column: Consultant Profile Card */}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 24
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="lg:col-span-5">
            <div className="bg-card border border-border rounded-2xl p-6 sm:p-8 shadow-sm space-y-6">
              <div className="aspect-[4/3] w-full rounded-xl overflow-hidden bg-muted relative">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/Seo-Consultant.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/Seo-Consultant.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/Seo-Consultant.png-wvc-sizes" alt="Yassine Essaadi - Independent Ecommerce SEO Consultant" className="w-full h-full object-cover object-top" loading="lazy" />
              </div>

              <div className="space-y-1">
                <h3 className="text-xl font-bold font-default text-foreground">
                  Yassine Essaadi
                </h3>
                <p className="text-sm text-primary font-medium">
                  Independent Ecommerce SEO Consultant
                </p>
                <p className="text-xs text-muted-foreground pt-2 leading-relaxed">
                  Direct consultant access with no account managers or agency hand-offs. I work directly on your catalogue, technical foundations and search strategy.
                </p>
              </div>

              <div className="pt-4 border-t border-border flex items-center justify-between text-xs text-muted-foreground">
                <span>Remote consultant</span>
                <span>UK &amp; International retailers</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}